import { ol_Feature } from "@omnia/exports/openlayers"
import { DatalayersMap, defineComponent, ICrop, IDatalayerContext, ILayerDetailsContext, IOmniaShell, isArrayEmpty, LayerViewModel, LocaleString, MgrLayer, MgrLayerType } from "../omnia"
import { IDatalayerMapDependencies, IDatalayersService } from "../types"
import { deleteLayers, enableOrDisableLayers } from "./actions"
import { collapseGroups, createState, getSelectedLayers, hasCollapsedGroups, selectCropId, selectFieldGroup, selectFields, selectGroup, selectGrouping1, selectGrouping2, selectLayer, selectLayerType, selectYear } from "./state"
import { LayerGroup, LayerGrouping, State } from "./types"

export class LayerManagerCtrl extends omnia.shell.Component {

    static create(): ng.IDirective {

        return defineComponent(LayerManagerCtrl, "app/datalayers/mgr/mgr.html")

    }

    public map: DatalayersMap
    private layerContext: IDatalayerContext

    public state: State

    public typePopup = false
    public types: MgrLayerType[]
    public type: MgrLayerType

    public years: number[]
    public year: number

    public crops: ICrop[]
    public cropId: number

    public layers: MgrLayer[]
    public layerGroups: LayerGroup[]

    public groupings1: LayerGrouping[]
    public grouping1: LayerGrouping

    public groupings2: LayerGrouping[]
    public grouping2: LayerGrouping

    public fields: State["fields"]
    public fieldGroups: State["fieldGroups"]
    public allFieldsSelected: boolean

    public lastDisplayedLayer: MgrLayer

    private _updatingMap = false

    static $inject = ["$scope", "OmniaDatalayersService", "OmniaDatalayersDependencies", "OmniaShell"]

    constructor($scope: ng.IScope, private layerSvc: IDatalayersService, private mapDependencies: IDatalayerMapDependencies, private shell: IOmniaShell) {

        super($scope)

        this._icon = "fas fa-layer-group"
        this._title = "Layer manager" as LocaleString

    }

    protected $onInit() {

        const map = this.map = new DatalayersMap(this.mapDependencies)
        this.addDisposable(() => this.map.dispose())

        // Clustering test - not used yet
        // const clusters = clusterFieldFeatures(map.fieldsLayer.getFeatures(), g => this.mapDependencies.topology.getCentroid(g))
        // console.log("clusters", clusters.map(c => ({ centroid: c.centroid, groups: c.features.map(f => f.field.fieldGroup)})))

        map.croppingLayer.selectable = false
        map.fieldsLayer.selectable = true
        map.enableMultipleSelection = true
        map.setTopmostLayer(map.fieldsLayer)

        // Cropping layer style - not used yet
        // const style = new CroppingStyle(map.croppingLayer)
        // map.croppingLayer.styleProvider = (selected) => style.getLayerStyleFunction(selected)

        this.addDisposable(map.watchSelection(f => this.updateSelectionFromMap(f)))

        this.layerContext = this.mapDependencies.createContext(() => [])

        const fields = new Map(this.layerSvc.fields.map(f => [f.fieldId, f]))

        createState(
            this.layerSvc,
            l => new LayerViewModel(this.layerContext, this.map, l, fields.get(l.FieldId)),
            this.map
        ).then(s => {

            this.setState(s)
            this.updateMapSelection()
            this.setupDetailsBox()

        })

    }


    private setState(state: State) {

        const {
            allTypes,
            type,
            layers,
            layerGroups,
            groupings,
            grouping1,
            grouping2,
            years,
            year,
            crops,
            cropId,
            fields,
            fieldGroups
        } = state

        this.state = state
        this.types = allTypes
        this.type = type
        this.layers = layers
        this.layerGroups = layerGroups

        this.groupings1 = groupings
        this.grouping1 = grouping1

        this.groupings2 = groupings.filter(g => g != grouping1)
        this.grouping2 = grouping2

        this.years = years
        this.year = year
        this.crops = crops
        this.cropId = cropId

        this.fields = fields
        this.fieldGroups = fieldGroups
        this.allFieldsSelected = fields?.every(f => f.selected)

    }

    selectType(type: MgrLayerType) {
        this.typePopup = false
        this.setState(selectLayerType(this.state, type?.key))
    }

    updateOnYearChanged() {
        const { state, year } = this
        this.setState(selectYear(state, year))
    }

    updateOnCropChanged() {
        const { state, cropId } = this
        this.setState(selectCropId(state, cropId))
    }

    updateOnGroupingChanged(level: number) {

        const state = level <= 1
            ? selectGrouping1(this.state, this.grouping1)
            : selectGrouping2(this.state, this.grouping2)

        this.setState(state)

    }

    updateOnSelectionChanged(group: LayerGroup, layer: MgrLayer) {
        let { state } = this
        state = layer
            ? selectLayer(state, group, layer, layer.selected)
            : selectGroup(state, group, group.selected)
        this.setState(state)
    }

    toggleExpanded() {
        const { state } = this
        const collapse = !hasCollapsedGroups(state)
        this.setState(collapseGroups(state, collapse))
    }

    getNextExpandedStateCss() {

        return hasCollapsedGroups(this.state)
            ? "fas fa-angle-down"
            : "fas fa-angle-up"
    }

    toggleLayerVisibility(layer: MgrLayer, $event: ng.IAngularEvent) {

        $event.stopPropagation()

        const onVisible = () => {
            this.map.setTopmostLayer(layer.vm.mapLayer)
            this.map.viewExtent(layer.vm.getExtent())
        }

        const v = !layer.vm.visible
        const p = layer.vm.setVisible(v)

        this.lastDisplayedLayer = v
            ? layer
            : (layer === this.lastDisplayedLayer)
                ? undefined
                : this.lastDisplayedLayer

        if (v) {
            if (p) {
                p.then(() => onVisible())
            }
            else {
                onVisible()
            }
        }

    }

    resetView() {

        const { layers } = this.state

        for (const l of layers) {
            l.vm.visible = false
        }

        this.map.viewDefaultExtent()
        this.setState(collapseGroups(this.state, true))
        this.lastDisplayedLayer = undefined

    }

    hasSelectedLayers(active?: boolean) {
        const l = getSelectedLayers(this.state, active)
        return !isArrayEmpty(l)
    }


    private updateSelectionFromMap(features: ol_Feature[]) {

        if (this._updatingMap) {
            return
        }

        const fields = features.map(f => f.get("data")).filter(omnia.fields.isFieldViewModel)

        this.setState(selectFields(this.state, fields))

    }

    private updateMapSelection() {

        const { map, state } = this
        const { fields } = state

        try {
            this._updatingMap = true
            map.selectFeatures(map.findFeaturesWithData(fields.filter(f => f.selected).map(f => f.field)))
        }
        finally {
            this._updatingMap = false
        }

    }

    toggleAllFields() {
        const { state } = this
        let { fields } = state
        const s = fields.every(f => f.selected) ? [] : fields.map(f => f.field)
        this.setState(selectFields(this.state, s))
        this.updateMapSelection()
    }

    toggleFieldGroup(group: State["fieldGroups"][0]) {
        const { state } = this
        group.selected = !group.selected
        this.setState(selectFieldGroup(state, group))
        this.updateMapSelection()
    }

    deleteLayers() {

        const { state, shell } = this

        deleteLayers(state, shell).then(r => {

            if (r !== false) {
                this.setState(r)
            }
        })

    }

    enableOrDisableLayers(disable: boolean) {

        const { state, shell } = this

        enableOrDisableLayers(state, disable, shell).then(r => {

            if (r !== false) {
                this.setState(r)
            }
        })

    }

    getLayerTooltip(l: MgrLayer) {

        return [
            (l.layer.UpdatedOn ?? l.layer.CreatedOn)?.toLocaleString(),
            l.inactive ? "This layer has been disabled" : undefined
        ].filter(t => !!t).join("\n")

    }

    private setupDetailsBox() {

        this.map.detailsBox = {
            component: "omnia-datalayers-details",
            isVisible: () => this.map && this.map.showDistributions,
            context: <ILayerDetailsContext>{
                map: this.map,
                layers: this.lastDisplayedLayer ? [this.lastDisplayedLayer.vm] : [],
                getSelectedField: () => this.lastDisplayedLayer?.vm?.field
            }
        }

    }


}

